import { TrashIcon } from '@/_assets/icon-components';
import LogoutIcon from '@/_assets/icon-components/Logout';
import { CField } from '@/_components/CField';
import ConfirmModal from '@/_components/ConfirmModal';
import CPhoneInput from '@/_components/PhoneInput';
import BottomSheet from '@/_components/Sheet';
import customerService from '@/_service/customer.service';
import { authActions } from '@/_store/auth/auth.slice';
import { checkoutActions } from '@/_store/checkout/checkout.slice';
import { IRedux } from '@/_store/types';
import { FEATURED_BRANDS } from '@/_utils/Constants';
import { Button } from '@/components/ui/button';
import { toaster } from '@/components/ui/toaster';
import { useRouter } from '@/i18n/navigation';
import { Input, SimpleGrid, Stack, Text } from '@chakra-ui/react';
import { useTranslations } from 'next-intl';
import React, { Dispatch, FormEvent, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

const Mobile = ({
  isOpen,
  setIsOpen,
}: {
  isOpen: boolean;
  setIsOpen: Dispatch<boolean>;
}) => {
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    last_name: '',
    date_of_birth: '',
  });

  const { user } = useSelector((state: IRedux) => state.auth);
  const dispatch = useDispatch();
  const t = useTranslations();
  const router = useRouter();

  // Update formData when user data changes
  useEffect(() => {
    if (user) {
      const nameParts = user.name?.split(' ') || ['', ''];
      setFormData({
        name: nameParts[0] || '',
        phone: user.phone || '',
        last_name: nameParts[1] || '',
        date_of_birth: user.date_of_birth || '',
      });
    }
  }, [user]);

  const { name, phone, last_name, date_of_birth } = formData;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!user?.id) {
      toaster.error({
        title: 'Error',
        description: 'User ID is missing',
      });
      return;
    }

    const data = {
      phone: phone,
      date_of_birth: date_of_birth || null,
      name: `${name} ${last_name}`.trim(),
    };

    try {
      await customerService.update(user.id, data);
      dispatch(
        authActions.updateUser({
          name: data.name,
          date_of_birth: date_of_birth,
          shipperId: FEATURED_BRANDS['yaponamama'],
        })
      );
      toaster.success({
        title: t('successfully_edited'),
      });
      setIsOpen(false);
    } catch (error: any) {
      toaster.error({
        title: 'Failed to update user data',
        description: error?.data?.Error?.message,
      });
    }
  };

  const onProfileDelete = () => {
    if (!user?.id) {
      toaster.error({
        title: 'Error',
        description: 'User ID is missing',
      });
      return;
    }
    customerService.deleteProfile(user.id).then(() => {
      router.push('/');
      dispatch(authActions.logout());
      dispatch(checkoutActions.clear());
    });
  };

  const onLogout = () => {
    dispatch(authActions.logout());
    dispatch(checkoutActions.clear());
    setIsOpen(false);
    router.refresh();
  };

  return (
    <BottomSheet
      contentHeight='content'
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
    >
      <Stack height='full' px={4} gap={0}>
        <Text color={'text'} fontSize={24} fontWeight={700}>
          {t('edit_data')}
        </Text>

        <Stack gap={4} my={4} flex={1} asChild>
          <form onSubmit={onSubmit}>
            <Stack gap={4} flex={1} pb={10}>
              <CField label={t('name')} bgColor='background' color={'text'}>
                <Input
                  type='text'
                  className='peer'
                  placeholder=''
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      name: e.target.value,
                    }))
                  }
                />
              </CField>
              <CField label={t('phone_number')} bgColor='background' color={'text'} readOnly>
                <Input
                  type='tel'
                  className='peer'
                  value={formData.phone}
                />
              </CField>
              <CField label={t('birthday')} bgColor='background' color={'text'}>
                <Input
                  type='date'
                  className='peer'
                  placeholder=''
                  value={formData.date_of_birth}
                  onChange={(e) =>
                    setFormData((prevData) => ({
                      ...prevData,
                      date_of_birth: e.target.value,
                    }))
                  }
                />
              </CField>
            </Stack>

            <SimpleGrid columns={2} gap={4}>
              <Button
                h='56px'
                variant='outline'
                onClick={onLogout}
              >
                <LogoutIcon color='currentColor' />
                {t('logout')}
              </Button>
              {/* <Button
                h='56px'
                color='red'
                variant='outline'
                colorPalette='red'
                onClick={() => setShowDeleteConfirmation(true)}
              >
                <TrashIcon color='red' />
                {t('delete_profile')}
              </Button> */}
              <Button
                height={'56px'}
                // w='full'
                // mt={4}
                type='submit'
                bg='primary'
                color='primary.text'
                _hover={{ bg: 'primary.hover' }}
              >
                {t('save')}
              </Button>
            </SimpleGrid>

          </form>
        </Stack>
        <ConfirmModal
          title={t('delete_profile')}
          isWarning
          description={t('sure_delete_profile')}
          isOpen={showDeleteConfirmation}
          onClose={() => setShowDeleteConfirmation(false)}
          onConfirm={() => onProfileDelete()}
          text={{ confirm: 'delete', cancel: 'cancel' }}
        />
      </Stack>
    </BottomSheet>
  );
};

export default Mobile;
